// client/src/components/MessageModal.tsx - UPDATED VERSION WITH ANONYMOUS MESSAGING SUPPORT
import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { Link } from 'react-router-dom';

interface Props {
    expert: any;
    attorney: any;
    onClose: () => void;
}



export default function MessageModal({ expert, attorney, onClose }: Props) {
    const [message, setMessage] = useState('');
    const [caseTitle, setCaseTitle] = useState('');
    const [hospitalProvider, setHospitalProvider] = useState('');
    const [incidentDate, setIncidentDate] = useState('');
    const [caseType, setCaseType] = useState<'plaintiff' | 'defense' | 'not_specified'>('not_specified');
    const [urgency, setUrgency] = useState<'standard' | 'urgent' | 'emergency'>('standard');
    const [attachment, setAttachment] = useState<File | null>(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [anonymousMessage, setAnonymousMessage] = useState(false); // NEW: Anonymous messaging option
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const sendMessage = async () => {
        if (!message.trim() || !caseTitle.trim()) {
            setError('Please provide case title and message');
            return;
        }

        setSending(true);
        setError('');

        try {
            // Get current user
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                throw new Error('You must be logged in to send messages');
            }

            // DEBUG INFO - UNCOMMENT TO ENABLE
            // console.log('Current user:', user.id);
            // console.log('Attorney ID:', attorney.id);
            // console.log('Expert ID:', expert.id);
            // console.log('Anonymous message:', anonymousMessage);

            // Upload attachment if provided
            let attachmentUrl = null;
            if (attachment) {
                try {
                    const fileExt = attachment.name.split('.').pop();
                    const fileName = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${fileExt}`;

                    // DEBUG INFO - UNCOMMENT TO ENABLE
                    // console.log('Uploading file:', fileName);

                    // Upload with the authenticated user's session
                    const { data: uploadData, error: uploadError } = await supabase.storage
                        .from('case-documents')
                        .upload(fileName, attachment, {
                            cacheControl: '3600',
                            upsert: false
                        });

                    if (uploadError) {
                        console.error('Upload error details:', uploadError);
                        // Don't fail the whole operation for upload issues
                        console.warn('File upload failed, continuing without attachment');
                    } else {
                        const { data: urlData } = supabase.storage
                            .from('case-documents')
                            .getPublicUrl(fileName);
                        attachmentUrl = urlData.publicUrl;
                        // DEBUG INFO - UNCOMMENT TO ENABLE
                        // console.log('File uploaded successfully:', attachmentUrl);
                    }
                } catch (uploadErr: any) {
                    console.error('File upload error:', uploadErr);
                    // Don't fail the whole operation for upload issues
                    console.warn('File upload failed, continuing without attachment');
                }
            }

            // Modify message if anonymous option is selected
            let finalMessage = message;
            if (anonymousMessage) {
                finalMessage += '\n\n---\nThis inquiry is from an anonymous attorney to maintain confidentiality during initial contact.';
            }

            // Construct detailed message
            const fullMessage = `
CASE INQUIRY DETAILS:
--------------------
Case Title: ${caseTitle}
Hospital/Provider: ${hospitalProvider || 'Not specified'}
Incident Date: ${incidentDate || 'Not specified'}
Case Type: ${caseType === 'plaintiff' ? 'Plaintiff' : caseType === 'defense' ? 'Defense' : 'Not Specified'}
Urgency: ${urgency.charAt(0).toUpperCase() + urgency.slice(1)}

MESSAGE:
--------
${finalMessage}

${attachmentUrl ? `\nAttached Document: ${attachmentUrl}` : ''}
            `.trim();

            // Use expert's user_id directly from the expert object, with fallback lookup
            let expertUserId = expert.user_id;
            if (!expertUserId) {
                // Look up the real expert row (same ID) to get user_id privately
                const { data: expertRow, error: fetchErr } = await supabase
                    .from('experts')
                    .select('user_id, full_name, email')
                    .eq('id', expert.id)
                    .single();
                if (fetchErr || !expertRow?.user_id) {
                    console.error('Could not resolve expert.user_id', fetchErr);
                    throw new Error('Could not find expert recipient. Please refresh and try again.');
                }
                expertUserId = expertRow.user_id;
                // optional: use for email notification only (still keep UI anonymous)
                expert.full_name = expert.full_name ?? expertRow.full_name;
                expert.email = expert.email ?? expertRow.email;
            }

            // DEBUG INFO - UNCOMMENT TO ENABLE
            // console.log('Using expert user_id:', expertUserId);

            // Prepare message data - including the new is_conflict_notice field
            const messageData = {
                // Core message fields that exist
                attorney_id: attorney.id,
                expert_id: expert.id,
                sender_type: 'attorney' as const,
                message: fullMessage,
                read: false,
                created_at: new Date().toISOString(),

                // Case details
                case_title: caseTitle,
                hospital_provider: hospitalProvider || null,
                incident_date: incidentDate || null,
                case_type: caseType,
                urgency: urgency,
                attachment_url: attachmentUrl,

                // Required legacy fields from your schema
                sender_id: user.id,
                recipient_id: expertUserId,

                // New field for conflict notice tracking
                is_conflict_notice: false,
                is_anonymous: anonymousMessage,
            };

            // DEBUG INFO - UNCOMMENT TO ENABLE
            // console.log('=== MESSAGE INSERT DEBUG ===');
            // console.log('User ID:', user.id);
            // console.log('Attorney ID:', attorney.id);
            // console.log('Message data:', messageData);

            // Insert the message
            const { data: insertData, error: msgError } = await supabase
                .from('messages')
                .insert([messageData])
                .select();

            if (msgError) {
                console.error('Database insert error:', msgError);

                // Provide more specific error messages
                if (msgError.message?.includes('violates row-level security policy')) {
                    throw new Error('Permission denied. Please make sure you are properly logged in and have the correct permissions.');
                } else if (msgError.message?.includes('foreign key')) {
                    throw new Error('Invalid attorney or expert reference. Please try refreshing the page.');
                } else {
                    throw new Error(`Failed to save message: ${msgError.message}`);
                }
            }

            // DEBUG INFO - UNCOMMENT TO ENABLE
            // console.log('Message saved successfully:', insertData);

            // Add after successful message insert
            if (insertData && insertData[0]) {
                // Send email notification
                try {
                    const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8001').trim();
                    await fetch(`${apiUrl}/notify/message`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            recipient_email: expert.email || '',
                            recipient_name: expert.full_name || 'Expert',
                            sender_name: anonymousMessage ? 'Anonymous Attorney' : attorney?.name || 'Attorney',
                            case_title: caseTitle,
                            is_anonymous: anonymousMessage
                        })
                    });
                } catch (emailErr) {
                    console.log('Email notification failed (non-blocking):', emailErr);
                }
            }

            // Create contact record (non-blocking)
            try {
                const { error: contactError } = await supabase
                    .from('contacts')
                    .upsert([{
                        attorney_id: attorney.id,
                        expert_id: expert.id,
                        created_at: new Date().toISOString()
                    }], {
                        onConflict: 'attorney_id,expert_id'
                    });

                if (contactError) {
                    console.error('Contact creation error (non-blocking):', contactError);
                }
            } catch (contactErr) {
                console.error('Contact creation failed (non-blocking):', contactErr);
            }

            setSuccess(true);
            setTimeout(() => {
                onClose();
            }, 2000);

        } catch (err: any) {
            console.error('Send message error:', err);
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setSending(false);
        }
    };

    if (success) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
                    <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-gray-600">The expert will be notified of your inquiry.</p>
                    {anonymousMessage && (
                        <p className="text-sm text-blue-600 mt-2">
                            Your message was sent anonymously. Your identity will be revealed only if you choose to in follow-up messages.
                        </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">
                        Expert identity will be revealed when they respond, or they may send a conflict notice to decline anonymously.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto overscroll-contain" onClick={onClose}>
            <div className="min-h-full flex items-start sm:items-center justify-center p-4">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-4 sm:my-8 relative" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">Contact Expert for Case Consultation</h3>
                            <p className="text-sm text-gray-600 mt-1">Anonymous Expert - {expert.specialty}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Link to how-it-works */}
                    <div className="text-center mb-4">
                        <Link
                            to="/how-it-works"
                            target="_blank"
                            className="text-blue-600 hover:text-blue-700 text-sm underline"
                        >
                            Not sure how it works? Watch our walkthrough video
                        </Link>
                    </div>

                    {/* Updated Privacy Notice */}
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    <strong>Privacy Protection:</strong> The expert's identity will remain anonymous until they respond to your inquiry.
                                    You can also choose to keep your identity anonymous during initial contact.
                                    If they have a conflict of interest, they can decline your case anonymously through a conflict notice without revealing their identity.
                                    Once they reply with interest, full contact information will be shared with both parties for direct communication.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* NEW: Anonymous Message Option */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <label className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                checked={anonymousMessage}
                                onChange={(e) => setAnonymousMessage(e.target.checked)}
                                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">
                                Send as anonymous attorney
                            </span>
                                <p className="text-xs text-gray-600 mt-1">
                                    Check this box to hide your identity from the expert during initial contact.
                                    Your firm name and personal details will not be shared until you choose to reveal them in follow-up messages.
                                    This can be useful for sensitive cases or preliminary inquiries.
                                </p>
                            </div>
                        </label>
                    </div>

                <div className="space-y-4">
                    {/* Case Information Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Case Information</h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Case Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={caseTitle}
                                    onChange={(e) => setCaseTitle(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., Smith v. Memorial Hospital"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Hospital/Provider
                                </label>
                                <input
                                    type="text"
                                    value={hospitalProvider}
                                    onChange={(e) => setHospitalProvider(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., St. Mary's Hospital"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Incident Date
                                </label>
                                <input
                                    type="date"
                                    value={incidentDate}
                                    onChange={(e) => setIncidentDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Case Type
                                </label>
                                <select
                                    value={caseType}
                                    onChange={(e) => setCaseType(e.target.value as 'plaintiff' | 'defense' | 'not_specified')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="not_specified">Not Specified</option>
                                    <option value="plaintiff">Plaintiff</option>
                                    <option value="defense">Defense</option>
                                </select>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Urgency Level
                                </label>
                                <div className="flex space-x-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="standard"
                                            checked={urgency === 'standard'}
                                            onChange={(e) => setUrgency(e.target.value as any)}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">Standard</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="urgent"
                                            checked={urgency === 'urgent'}
                                            onChange={(e) => setUrgency(e.target.value as any)}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-orange-600">Urgent</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="emergency"
                                            checked={urgency === 'emergency'}
                                            onChange={(e) => setUrgency(e.target.value as any)}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-red-600">Emergency</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Message Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={6}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={anonymousMessage
                                ? `Dear Expert,\n\nI am seeking an expert witness for a ${caseType === 'not_specified' ? '' : caseType} medical malpractice case involving ${expert.specialty}. The case involves...\n\nI would appreciate the opportunity to discuss this case with you and determine if you would be available to assist.\n\nBest regards,\nAnonymous Attorney`
                                : `Dear Expert,\n\nI am seeking an expert witness for a ${caseType === 'not_specified' ? '' : caseType} medical malpractice case involving ${expert.specialty}. The case involves...\n\nI would appreciate the opportunity to discuss this case with you and determine if you would be available to assist.\n\nBest regards,\n${attorney.name}`
                            }
                        />
                    </div>

                    {/* Attachment Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Case Summary/Documentation (Optional)
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                            <div className="space-y-1 text-center">
                                <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    stroke="currentColor"
                                    fill="none"
                                    viewBox="0 0 48 48"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div className="flex text-sm text-gray-600">
                                    <label
                                        htmlFor="file-upload"
                                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                                    >
                                        <span>Upload a file</span>
                                        <input
                                            id="file-upload"
                                            name="file-upload"
                                            type="file"
                                            className="sr-only"
                                            accept=".pdf,.doc,.docx"
                                            onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                                        />
                                    </label>
                                    <p className="pl-1">or drag and drop</p>
                                </div>
                                <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                                {attachment && (
                                    <p className="text-sm text-green-600 mt-2">
                                        ✔ {attachment.name}
                                    </p>
                                )}
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            Note: File upload is optional. If upload fails, your message will still be sent.
                        </p>
                    </div>

                    {/* Anonymous Message Warning */}
                    {anonymousMessage && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded">
                            <p className="text-sm">
                                <strong>Anonymous Mode:</strong> Your identity, firm name, and contact information will not be shared with the expert.
                                You can reveal your identity later in follow-up messages if you choose to proceed.
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                            {error}
                        </div>
                    )}

                    <div className="flex space-x-3 pt-4">
                        <button
                            data-testid="send-message"
                            onClick={sendMessage}
                            disabled={sending || !caseTitle.trim() || !message.trim()}
                            className={`flex-1 py-2 px-4 rounded-lg text-white font-medium transition ${
                                sending || !caseTitle.trim() || !message.trim()
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : urgency === 'emergency'
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : urgency === 'urgent'
                                            ? 'bg-orange-600 hover:bg-orange-700'
                                            : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {sending ? 'Sending...' : anonymousMessage ? 'Send Anonymous Message' : 'Send Message'}
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
}